import { useEffect, useRef } from "react";
import { toast } from "sonner";
import * as serviceWorkerRegistration from "@/serviceWorkerRegistration";

// Manages PWA updates so installed shortcuts never serve a stale build:
//  - shows a persistent "New version available → Refresh" toast for active sessions
//  - auto-applies the update when the app is re-opened/refocused (relaunched shortcut)
export default function ServiceWorkerUpdater() {
  const waitingRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const activateUpdate = () => {
      const waiting = waitingRef.current;
      if (!waiting) return;
      waiting.postMessage({ type: "SKIP_WAITING" });
    };

    // When the new SW takes control, reload once to load the fresh build.
    const onControllerChange = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    serviceWorkerRegistration.register({
      onUpdate: (registration) => {
        waitingRef.current = registration.waiting;
        toast("A new version is available", {
          description: "Refresh to get the latest features and fixes.",
          duration: Infinity,
          action: { label: "Refresh", onClick: activateUpdate },
        });
      },
    });

    // Relaunched shortcut / tab refocused → check for a new deploy and apply it
    // automatically (the user just brought the app to the foreground).
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        await reg.update();
        if (reg.waiting) {
          waitingRef.current = reg.waiting;
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      } catch { /* offline or unsupported — ignore */ }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return null;
}
